import {
  HHButton,
  HHError,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { DataGridPro, GridPaginationModel } from '@mui/x-data-grid-pro';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  ClientType,
  InsurerType,
  PartnershipType,
  PaymentType,
  useDeleteProcedureCodeMutation,
  useUpdateProcedureCodeMutation,
} from '../../types';
import { NoRowsOverlay } from '../grid-components/no-rows-or-error-overlay';
import { ProcedureCodeApplicabilityDrawer } from './procedure-code-applicability-drawer';
import {
  ProcedureCodeFilterBar,
  ProcedureCodeFilterParams,
} from './procedure-code-filter-bar';
import { ProcedureCodeForm } from './procedure-code-form/form';
import {
  ProcedureCodeFormValues,
  ValidatedProcedureCodeFormValues,
} from './procedure-code-form/validation-schema';
import { useProcedureCodeColumns } from './use-procedure-code-columns';
import {
  ProcedureCodeRow,
  useProcedureCodeQueries,
} from './use-procedure-code-queries';

export interface ProcedureCodeGridProps {
  filterParams: ProcedureCodeFilterParams;
  setFilterParams: Dispatch<SetStateAction<ProcedureCodeFilterParams>>;
  insurers: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  insurersLoading: boolean;
  partnerships: PartnershipType[];
  partnershipsLoading: boolean;
  clients: ClientType[];
  clientsLoading: boolean;
  paymentTypes: PaymentType[];
  paymentTypesLoading: boolean;
}

const DEFAULT_PAGE_SIZE = 25;

export const ProcedureCodeGrid = ({
  filterParams,
  setFilterParams,
  insurers,
  insurersLoading,
  partnerships,
  partnershipsLoading,
  clients,
  clientsLoading,
  paymentTypes,
  paymentTypesLoading,
}: ProcedureCodeGridProps): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setDialogContent, closeDialog } = useDynamicDialogContext();
  const { setSnackbarState } = useSnackbarContext();

  const [updateProcedureCode, { data: updateData, error: updateError }] =
    useUpdateProcedureCodeMutation();
  const [deleteProcedureCode] = useDeleteProcedureCodeMutation();

  useEffect(() => {
    if (!updateError) {
      return;
    }
    setSnackbarState('Error updating CPT code', 'error', 3000);
  }, [updateError, setSnackbarState]);

  useEffect(() => {
    if (!updateData) {
      return;
    }
    setSnackbarState('CPT code updated successfully', 'success', 3000);
    onClose();
  }, [updateData, setSnackbarState, onClose]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const handleFiltersApplied = useCallback((): void => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const { rows, loading, hasError, total } = useProcedureCodeQueries(
    paginationModel,
    filterParams,
  );

  const handleEdit = useCallback(
    (row: ProcedureCodeRow): void => {
      // ACCOUNTS-AND-PLAN-MIGRATION: row.insurerId, row.partnershipId, row.clientId are client/insurer/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
      const initialValues: ProcedureCodeFormValues = {
        insurerId: row.insurerId ?? null,
        cptCode: row.cptCode,
        paymentTypeId: row.paymentTypeId,
        partnershipId: row.partnershipId ?? null,
        clientId: row.clientId ?? null,
        modifier1: row.modifier1 ?? '',
        modifier2: row.modifier2 ?? '',
      };

      const handleUpdate = async (
        values: ValidatedProcedureCodeFormValues,
      ): Promise<void> => {
        await updateProcedureCode({
          variables: {
            id: row.id,
            procedureCodeInput: {
              insurerId: values.insurerId,
              cptCode: values.cptCode,
              paymentTypeId: values.paymentTypeId,
              partnershipId: values.partnershipId,
              clientId: values.clientId,
              modifier1: values.modifier1 || null,
              modifier2: values.modifier2 || null,
            },
          },
          refetchQueries: ['getProcedureCodes'],
        });
      };

      setDrawerContent(
        <HHStack padding={4}>
          <ProcedureCodeForm
            onSubmit={handleUpdate}
            initialValues={initialValues}
            id={row.id}
            insurers={insurers}
            insurersLoading={insurersLoading}
            partnerships={partnerships}
            partnershipsLoading={partnershipsLoading}
            clients={clients}
            clientsLoading={clientsLoading}
            paymentTypes={paymentTypes}
            paymentTypesLoading={paymentTypesLoading}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">Edit CPT Code</HHTypography>,
        undefined,
        'temporary',
      );
    },
    [
      setDrawerContent,
      updateProcedureCode,
      insurers,
      insurersLoading,
      partnerships,
      partnershipsLoading,
      clients,
      clientsLoading,
      paymentTypes,
      paymentTypesLoading,
    ],
  );

  const handleViewApplicability = useCallback(
    (row: ProcedureCodeRow): void => {
      setDrawerContent(
        <HHStack padding={4}>
          {/* ACCOUNTS-AND-PLAN-MIGRATION: row.partnershipId, row.insurerId, row.clientId are client/insurer/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration. */}
          <ProcedureCodeApplicabilityDrawer
            partnershipId={row.partnershipId}
            insurerId={row.insurerId}
            clientId={row.clientId}
            cptCode={row.cptCode}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">CPT Code Applicability</HHTypography>,
        '50%',
        'temporary',
      );
    },
    [setDrawerContent],
  );

  const handleDelete = useCallback(
    (row: ProcedureCodeRow): void => {
      const executeDelete = async (): Promise<void> => {
        closeDialog();
        try {
          await deleteProcedureCode({
            variables: { id: row.id },
            refetchQueries: ['getProcedureCodes'],
          });
          setSnackbarState('CPT code deleted successfully', 'success', 3000);
        } catch {
          setSnackbarState('Error deleting CPT code', 'error', 3000);
        }
      };

      setDialogContent({
        title: (
          <HHTypography hhVariant="section-title">Delete CPT Code</HHTypography>
        ),
        content: (
          <HHStack spacing={2}>
            <HHTypography hhVariant="body1">
              Are you sure you want to delete this CPT code ({row.cptCode})?
            </HHTypography>
          </HHStack>
        ),
        footer: (
          <HHStack direction="row" spacing={2} padding={1}>
            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              onClick={closeDialog}
              data-testid="procedure-code-delete-cancel-button"
            >
              Cancel
            </HHButton>
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              color="error"
              onClick={(): void => {
                void executeDelete();
              }}
              data-testid="procedure-code-delete-confirm-button"
            >
              Delete
            </HHButton>
          </HHStack>
        ),
        width: 'sm',
        fullWidth: true,
      });
    },
    [setDialogContent, closeDialog, deleteProcedureCode, setSnackbarState],
  );

  const columns = useProcedureCodeColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    onViewApplicability: handleViewApplicability,
    canWrite,
  });

  if (hasError) {
    return (
      <HHError body="Error fetching CPT codes" hhVariant="variant-bypass" />
    );
  }

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={6}
      justifyContent="space-between"
      minHeight="0"
    >
      <HHStack paddingLeft={6}>
        <ProcedureCodeFilterBar
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          onFiltersApplied={handleFiltersApplied}
        />
      </HHStack>

      <DataGridPro
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row): number => row.id}
        rowCount={total}
        pagination
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        filterMode="server"
        sortingMode="server"
        disableRowSelectionOnClick
        pinnedColumns={{ right: ['actions'] }}
        aria-label="CPT codes table"
        {...(process.env.NODE_ENV === 'test' && {
          disableVirtualization: true,
        })}
        slots={{
          noResultsOverlay: NoRowsOverlay,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          noResultsOverlay: { error: hasError } as Record<string, boolean>,
          noRowsOverlay: { error: hasError } as Record<string, boolean>,
        }}
      />
    </HHStack>
  );
};
